import React, { useState, useEffect, useRef } from "react";
import { TextInput, NewNativeSelectInput, NumberInput } from "../ui/Input";
import { FaCaretDown } from "react-icons/fa";
import { Button } from "../ui/Button";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/TableTags";
import useToaster from "../../hooks/useToaster";
import { debounce } from "lodash";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { Popover, PopoverTrigger } from "../ui/Popover";
import DropdownCheckbox from "../ui/DropdownCheck";
import { MdClose } from "react-icons/md";
import { checkIsEqual } from "../../lib/common";

/**
 * Form component for editing journal details
 *
 * @param {Object} props - Component props
 * @param {Object} props.initialJournalData - Initial journal data to edit
 * @param {Function} props.oncancel - Cancel handler function
 * @param {Function} props.onSave - Save handler function
 * @param {string} props.active1 - Active parent tab name
 * @param {string} props.active2 - Active child tab name
 * @param {Array<string>} props.teams - Available team options
 * @param {Array<Object>} props.journalManagers - Available journal manager options
 */

const EditJournalForm = ({
  initialJournalData,
  oncancel,
  onSave,
  active1,
  onchange,
  active2,
  teams,
  journalManagers,
  journalNames = [],
}) => {
  /**
   * object to store journal data
   */

  const formRef = useRef(null);

  const [hasChanges, setHasChanges] = useState(false);

  const [journalData, setJournalData] = useState({
    ...initialJournalData,
  });

  const [initialFormData] = useState({ ...initialJournalData });

  /**
   * Toggles manual override for a specific field configuration
   *
   * @param {number} index - Index of the field configuration to update
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleManualOverride = (index) => {
    setJournalData((prev) => {
      const updatedFieldConfig = [...(prev.fieldConfiguration || [])];
      updatedFieldConfig[index] = {
        ...updatedFieldConfig[index],
        showField: !updatedFieldConfig[index]?.showField,
      };
      return {
        ...prev,
        fieldConfiguration: updatedFieldConfig,
      };
    });
  };

  useEffect(() => {
    const isChanged = checkIsEqual(journalData, initialFormData);
    // JSON.stringify(journalData) !== JSON.stringify(initialFormData);
    setHasChanges(!isChanged);
  }, [journalData, initialFormData]);

  const [errors, setErrors] = useState({
    journalName: "",
    acronym: "",
  });

  let [popover, setPopover] = useState(false);
  let [selectedName, setSelectedName] = useState("");
  let [searchValue, setSearchValue] = useState("");
  let [hasErr, setHasErr] = useState(false);

  const showToaster = useToaster();
  useEffect(() => {
    const formElement = formRef.current;

    const handleScroll = () => {
      if (popover) {
        setPopover(false);
      }
    };

    if (formElement) {
      formElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [popover]);
  useEffect(() => {
    if (
      journalData.journalManager.length > 0 &&
      typeof journalData.journalManager !== "string"
    ) {
      const names = journalData.journalManager
        .map((val) => {
          const match = journalManagers.find((j) => j.value === val);
          return match?.label?.split(" -")[0].trim();
        })
        .filter(Boolean);

      if (names.length === 1) {
        setSelectedName(names[0]);
      } else {
        const moreCount = names.length - 1;
        setSelectedName(`${names[0]} + ${moreCount} more`);
      }
    } else {
      setSelectedName("");
    }
  }, [journalManagers, journalData.journalManager]);

  const handleManagerChange = (selected) => {
    setJournalData((prev) => ({
      ...prev,
      journalManager: selected,
    }));
  };

  const validateJournalName = debounce((value) => {
    const trimmedValue = value.trim();

    if (
      trimmedValue !== "" &&
      journalNames.some(
        (name) => name.toLowerCase() === trimmedValue.toLowerCase()
      )
    ) {
      setErrors((prev) => ({
        ...prev,
        journalName: "Journal name already exists",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        journalName: "",
      }));
    }
  }, 300);

  /**
   * Validates and initializes fieldConfiguration array
   * Ensures fieldConfiguration is always an array, even if not provided
   * @listens journalData.fieldConfiguration
   */
  useEffect(() => {
    if (
      journalData.fieldConfiguration === undefined ||
      journalData.fieldConfiguration === null
    ) {
      setJournalData((prev) => ({
        ...prev,
        fieldConfiguration: [],
      }));
    }
  }, []);

  /**
   * Handles input changes for form fields
   * Updates journalData state with new values while preserving other fields
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event
   * @param {string} e.target.name - Name of the changed field
   * @param {string|number} e.target.value - New value of the field
   */
  const handleChange = (e) => {
    const { name, type, value } = e.target;
    if (name === "journalName" && value !== initialJournalData?.journalName) {
      validateJournalName(value);
    }
    setJournalData((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  useEffect(() => {
    if (Object.values(errors).some((value) => value !== "")) {
      setHasErr(true);
    } else {
      setHasErr(false);
    }
  }, [errors]);

  /**
   * Validates and submits the journal form
   * - Trims all text inputs
   * - Validates required fields
   * - Formats fieldConfiguration data
   * - Calls onSave with processed data
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submission event
   * @throws {Error} Shows toaster message if validation fails
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!navigator.onLine) {
      showToaster("You are currently offline. Please check your internet connection!");
      return;
    }

    setIsSubmitting(true);
    try {
      const requiredFields = [
        { key: "journalName", label: "Journal Name" },
        { key: "team", label: "Team" },
        { key: "journalManager", label: "Journal Manager" },
        { key: "url", label: "URL" },
      ];

      const trimmedData = { ...journalData };

      let hasErrors = false;
      const newErrors = {};
      const missingFields = [];

      requiredFields.forEach(({ key, label }) => {
        let value = journalData[key];

        if (typeof value === "string") {
          value = value.trim();
          trimmedData[key] = value;
        }

        const isEmpty =
          value === null ||
          value === undefined ||
          (typeof value === "string" && value === "") ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          newErrors[key] = "This field is required";
          missingFields.push(label);
          hasErrors = true;
        } else {
          newErrors[key] = "";
        }
      });

      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };

        for (const key in newErrors) {
          if (!prevErrors[key]) {
            updatedErrors[key] = newErrors[key];
          }
        }

        return updatedErrors;
      });

      if (hasErrors) {
        const fieldsText = missingFields.join(", ");
        const message =
          missingFields.length === 1
            ? `Please fill the required field: ${fieldsText}`
            : `Please fill the required fields: ${fieldsText}`;
        showToaster(message);
        return;
      } else await onSave(e, trimmedData);
    } catch (error) {
      console.error("Error saving journal:", error);
      showToaster(
        "An error occurred while saving the journal. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const fieldConfigurations =
    journalData.fieldConfiguration?.filter(
      (field) =>
        field.articleOrIssue === "article" || field.articleOrIssue === "both"
    ) || [];

  /**
   * Configuration for field configuration table columns
   * @type {Array<Object>}
   * @property {string} key - Data key for the column
   * @property {string} value - Display name for the column
   * @property {string} width - Column width in pixels
   */
  const headers = [
    { key: "property", value: "FIELD NAME", width: "240px" },
    // { key: "articleOrIssue", value: "CATEGORY", width: "150px" },
    // { key: "fieldType", value: "DATA TYPE", width: "150px" },
    // { key: "method", value: "METHOD", width: "150px" },
    // { key: "manualOverwrite", value: "MANUAL OVERRIDE", width: "160px" },
    { key: "showField", value: "SHOW FIELD", width: "160px" },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-[calc(100vh-80px)] bg-white"
    >
      {/* Header */}
      <div className="h-[64px] px-4">
        <div className="flex flex-row items-center">
          <button onClick={oncancel}>
            <span className="text-[#868E94] hover:underline text-[13px] font-medium px-1 h-[16px]">
              Settings
            </span>
          </button>
          <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px]" />
          <button onClick={oncancel}>
            <span className="text-[#868E94] hover:underline text-[13px] font-medium px-1 h-[16px]">
              Journal
            </span>
          </button>
          <MdOutlineKeyboardArrowRight className="text-[#5D6871] text-[16px]" />
          <span className="text-[#868E94] hover:underline text-[13px] font-medium px-1 h-[16px]">
            {initialJournalData.journalName.slice(0, 15)}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-[23px] h-[28px] text-[#20282E] font-semibold">
            {journalData.id ? "Journal Details" : ""}
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-4">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div ref={formRef} className={`flex-1 overflow-y-auto pb-8 `}>
            {/* Journal Information Section */}
            <div className="space-y-4 mb-4">
              <h2 className="text-xl text-[18px] h-[24px] text-[#5D6871]">
                Journal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <TextInput
                  label="Journal"
                  name="journalName"
                  value={journalData.journalName}
                  onChange={handleChange}
                  placeholder="Enter journal"
                  error={errors.journalName}
                  important={true}
                  maxLength={100}
                />
                <div>
                  <label className="block text-[#35424D] text-[16px]">
                    Production Editor(s) <span className="text-red-500">*</span>
                  </label>
                  <Popover
                    open={popover}
                    onOpenChange={(open) => {
                      if (!open) {
                        setJournalData((prev) => ({
                          ...prev,
                          journalManager: [...prev.journalManager],
                        }));
                      }
                      setPopover(open);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <div className="max-w-[310px] h-[40px] p-2 mt-2 border flex items-center justify-between border-gray-300 rounded-md text-[#868E94] focus-within:border-[#35424D] focus-within:outline-none">
                        <input
                          name="searchValue"
                          value={searchValue}
                          onChange={(e) =>
                            popover && setSearchValue(e.target.value)
                          }
                          className="focus:outline-none h-[30px] w-[300px]"
                          placeholder={
                            selectedName
                              ? selectedName
                              : "Select production editor(s)"
                          }
                        />
                        {searchValue.length <= 0 ? (
                          <FaCaretDown
                            className={`text-gray-500 text-[18px] `}
                          />
                        ) : (
                          <MdClose
                            className={`text-gray-500 text-[18px] `}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchValue("");
                            }}
                          />
                        )}
                      </div>
                    </PopoverTrigger>
                    <DropdownCheckbox
                      options={journalManagers}
                      selectedValues={journalData.journalManager}
                      onSelectionChange={handleManagerChange}
                      onClose={() => setPopover(false)}
                      width="w-[310px]"
                      searchBar={false}
                      search={searchValue.trim()}
                      setSearch={setSearchValue}
                      type="Journal"
                    />
                  </Popover>
                </div>
                <NewNativeSelectInput
                  label="Team"
                  name="team"
                  value={journalData.team}
                  onChange={handleChange}
                  options={teams}
                  placeholder="Select Team"
                  important={true}
                />
              </div>
              <TextInput
                label="URL Abbreviation"
                name="url"
                value={journalData.url}
                onChange={handleChange}
                required
                placeholder="Enter Journal Url"
                maxLength={200}
              />
            </div>

            {/* Journal Configuration Section */}
            <div className="space-y-4 mb-4">
              <h2 className="text-xl text-[18px] h-[24px] text-[#5D6871]">
                Journal Configuration
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <NumberInput
                  label="Cover subs due"
                  name="coverSubInvitees"
                  value={journalData.coverSubInvitees}
                  onChange={handleChange}
                  placeholder="No. of days before publishing"
                  maxValue={99}
                />
                <NumberInput
                  label="Cover due to TNQ"
                  name="coverSubmissionDue"
                  value={journalData.coverSubmissionDue}
                  onChange={handleChange}
                  placeholder="No. of days before publishing"
                  maxValue={99}
                />
                <NumberInput
                  label="Issue holdout"
                  name="issueHoldout"
                  value={journalData.issueHoldout}
                  onChange={handleChange}
                  placeholder="No. of days before publishing"
                  maxValue={99}
                />
                <NumberInput
                  label="Last MS for SCE"
                  name="lastMsForRed"
                  value={journalData.lastMsForRed}
                  onChange={handleChange}
                  placeholder="No. of days before publishing"
                  maxValue={99}
                />
                <NumberInput
                  label="Issue finalizing"
                  name="issueFinalizing"
                  value={journalData.issueFinalizing}
                  onChange={handleChange}
                  placeholder="No. of days before publishing"
                  maxValue={99}
                />
                <NumberInput
                  label="st@r form due"
                  name="noOfDayBeforeStarFromDue"
                  value={journalData.noOfDayBeforeStarFromDue}
                  onChange={handleChange}
                  placeholder="No. of days before publishing"
                  maxValue={99}
                />
              </div>
            </div>

            {/* Field Configuration Table */}
            {fieldConfigurations.length > 0 && (
              <>
                <h2 className="text-xl h-[24px] text-[18px] text-[#5D6871] mb-4">
                  Field Configuration
                </h2>
                <div className="overflow-x-auto rounded-sm mb-6">
                  <Table className="w-full bg-white border border-[#D7D9DB]">
                    <TableHeader className="bg-[#F8F8F8]">
                      <TableRow>
                        {Array.from({ length: 3 }).map((_, index) => (
                          <React.Fragment key={index}>
                            <TableHead className="h-[40px] border-b border-l border-[#D7D9DB] px-4 py-2 text-[11px] font-semibold text-left text-[#35424D]">
                              FIELD NAME
                            </TableHead>
                            <TableHead className="h-[40px] border-b border-l border-[#D7D9DB] px-4 py-2 text-[11px] font-semibold text-left text-[#35424D]">
                              SHOW FIELD
                            </TableHead>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody className="text-[#35424D] divide-y divide-gray-200">
                      {Array.from({
                        length: Math.ceil(fieldConfigurations.length / 3),
                      }).map((_, rowIndex) => {
                        const start = rowIndex * 3;
                        const fieldsChunk = fieldConfigurations.slice(
                          start,
                          start + 3
                        );

                        return (
                          <TableRow key={rowIndex} className="hover:bg-gray-50">
                            {fieldsChunk.map((field, colIndex) => (
                              <React.Fragment key={colIndex}>
                                <TableCell className="py-3 px-4 font-semibold text-[13px] border-r border-b border-[#D7D9DB]">
                                  {field.property}
                                </TableCell>
                                <TableCell className="py-3 px-4 border-r border-b border-[#D7D9DB]">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleToggleManualOverride(
                                        start + colIndex
                                      )
                                    }
                                    className={`w-8 h-4 rounded-full p-0.5 flex items-center transition-colors ${
                                      field.showField
                                        ? "bg-blue-500"
                                        : "bg-gray-300"
                                    }`}
                                  >
                                    <span
                                      className={`block w-3.5 h-3.5 bg-white rounded-full shadow-md transition-transform ${
                                        field.showField
                                          ? "translate-x-[16px]"
                                          : "translate-x-0"
                                      }`}
                                    />
                                  </button>
                                </TableCell>
                              </React.Fragment>
                            ))}

                            {/* Fill empty cells if last row has fewer than 3 fields */}
                            {fieldsChunk.length < 3 &&
                              Array.from({
                                length: 3 - fieldsChunk.length,
                              }).map((_, emptyIndex) => (
                                <React.Fragment key={emptyIndex}>
                                  <TableCell className="py-3 px-4 border-r border-b border-[#D7D9DB]"></TableCell>
                                  <TableCell className="py-3 px-4 border-r border-b border-[#D7D9DB]"></TableCell>
                                </React.Fragment>
                              ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>

          {/* Fixed Footer Buttons */}
          <div className="bottom-0 left-0 right-0 p-2 border-t mb-6 bg-white">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={oncancel}
                className="text-[#1C40C9] py-1 rounded-md hover:bg-gray-50 transition-colors mr-4"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!hasChanges || isSubmitting || hasErr}
                className={`px-4 py-2 rounded-md transition-colors bg-[#1C40C9] text-white  
                 ${
                   !hasChanges || hasErr
                     ? "opacity-50 cursor-not-allowed bg-gray-400"
                     : ""
                 }
                 ${isSubmitting ? "opacity-70 cursor-wait" : ""}`}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export { EditJournalForm };
